/**
 * React Query hook for fetching games on a specific date.
 */
import { useQuery } from '@tanstack/react-query';
import { mlbApi } from '../services/api';
import type { ScheduleResponse, ScheduleGame } from '../types/gameday';
import { STALE_TIMES } from '../types/statcast';

// Query key factory
export const scheduleKeys = {
  all: ['schedule'] as const,
  date: (date: string) => [...scheduleKeys.all, date] as const,
};

interface UseGamedayScheduleOptions {
  /** Date in YYYY-MM-DD format, or null to disable the query */
  date: string | null;
}

interface UseGamedayScheduleReturn {
  games: ScheduleGame[];
  isLoading: boolean;
  error: Error | null;
}

/**
 * Fetches games for a specific date.
 *
 * @param options.date - Date string in YYYY-MM-DD format, or null to disable
 * @returns Games array, loading state, and error state
 */
export function useGamedaySchedule({ date }: UseGamedayScheduleOptions): UseGamedayScheduleReturn {
  const { data, isLoading, error } = useQuery<ScheduleResponse>({
    queryKey: scheduleKeys.date(date ?? ''),
    queryFn: () => mlbApi.getSchedule(date!),
    enabled: date !== null,
    staleTime: STALE_TIMES.SCHEDULE, // 10 minutes
  });

  return {
    games: data?.games ?? [],
    isLoading,
    error: error as Error | null,
  };
}
